import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ProfileService } from 'src/app/core/services/profile.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../core/services/authentication.service';
import { CmsService } from '../core/services/cms.service';
import { LogCategory, LoggerService, LogService, LogType } from '../core/services/logger.service';

@Injectable()
export class AnnouncementsService {

    // A list of dismissed IDs that we store as a property here because we were lazy and weren't consuming this as an observable in the component. 
    public dismissedIds: string[];

    // This behavior subject is used to trigger the recalculation of the discrete list of dismissedIds (which we only have because we're lazy).
    // Once we no longer need that list, then this can go away, too. 
    public updateDismissals$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    // This is the stack of observables the service needs 
    private combinedAnnouncements$: Observable<any[]>;  // All types of announcements (Announcements, stops, etc) 
    private profileMerge$: Observable<any[]>;           // Adds in dismissed status from profile 
    private all$: Observable<any>;                      // The up-to-date complete list of announcements as objects that the UI can use. 
    public undismissed$: Observable<any>;               // Only those announcements that are not dismissed. 
    public dismissed$: Observable<any>;                 // Only those announcements that ARE dismissed 
    public summary$: Observable<any>;                   // up-to-date summary information about the announcements. 

    constructor(
        private http: HttpClient,
        private auth: AuthenticationService,
        private logger: LoggerService,
        private profile: ProfileService,
        private util: UtilityService,
        private cmsService: CmsService,
    ) {
        this.log("constructor", null, LogCategory.Constructor);

        // Combines all announcements into a single array (currently both financial holds and announceents)
        this.combinedAnnouncements$ = auth.loggedInUser$.pipe(
            switchMap(() => combineLatest(
                this.http.get<any[]>(environment.apiUrl + '/announcements/financialholds'),
                this.cmsService.getV2ContentByType$("Announcements"))
                .pipe(
                    map(combined => [...combined[0], ...combined[1]])
                ))
        );

        // The combined announcements are merged with the profile data to mark what's been dismissed. This adds the
        // isDismissed property to each annoumcement. 
        this.profileMerge$ = combineLatest(this.combinedAnnouncements$, profile.profile$)
            .pipe(
                map(([first, second]) => {
                    // Grab the "dismissed" data from the announcement data in the profile 
                    // Set this static list of dismissed IDs just incase the component needs it.
                    let nestedPath = "widgets.prefs.Announcements.Data.Dismissed";
                    if (second.version == 2)
                        nestedPath = "components.data.Announcements.Dismissed";
                    
                    let dismissedString = util.getNestedValue(second, nestedPath, "");
                    this.dismissedIds = dismissedString.split(",");

                    // Add and set the isDismissed property for each item on the list
                    for (let i = 0; i < first.length; i++) {
                        first[i].isDismissed = this.dismissedIds.indexOf(first[i].Id) > -1;
                    };

                    // Return only the original announcement data 
                    return first;
                })
            );

        // All the announcements, including whether or not they're dismissed in the profile. UpdateDismissals intercepts
        // at this point so that the settings in the profile don't overwrite the changes made in the UI 
        this.all$ = combineLatest(this.profileMerge$, this.updateDismissals$)
            .pipe(
                map(([items, temp]) => {

                    // Recalculate the dismissed item IDs based on the in-memory list of announcements, which includes UI changes 
                    this.dismissedIds = items.filter(a => a.isDismissed).map(b => b.Id);

                    // Return only the original announcement data 
                    return items;
                })
            );

        // This emits only those announcements that are NOT marked as dismissed 
        this.undismissed$ = this.all$.pipe(
            tap(a => { this.log("unread$", "Emitted", LogCategory.SubscriptionData); }),
            map((a: any[]) => { return a.filter(b => !!!b.isDismissed) })
        );

        // This emits only those announcements that ARE marked as dismissed 
        this.dismissed$ = this.all$.pipe(
            tap(a => { this.log("dismissed$", "Emitted", LogCategory.SubscriptionData); }),
            map((a: any[]) => { return a.filter(b => !!b.isDismissed) })
        );

        // This emits summary data about the announcements
        this.summary$ = this.all$.pipe(
            tap(a => { this.log("summary$", "Emitted", LogCategory.SubscriptionData); }),
            map((data: any[]) => {
                const dismissed = data.filter(a => !!a.isDismissed).length;
                const unread = data.length - dismissed;
                const s = (dismissed > 1) ? 's' : '';
                return {
                    dismissed,
                    unread,
                    revealMessage: (dismissed > 0) ? `${dismissed} dismissed item${s}` : '',
                }
            })
        );
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.Service, LogService.Announcements, category);
    }
}
